import type { AlignSetting } from '@webapp/common/lib/ui';
import type { SurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';
import { QuestionType } from '@webapp/common/resources/survey';

import { getApiClass } from '@webapp/survey/src/api/queries-api';
import type { QuestionActorRef, QuestionContext } from '@webapp/survey/src/machine/question-machine';

import css from './question.css';

const replaceMinMax = (tpl: string, min: number, max: number): string => {
    return tpl?.replace(/{min}/, String(min || '-')).replace(/{max}/, String(max || '-'));
};
const replacePoints = (tpl: string, points: number): string => tpl.replace(/{points}/, String(points));

export const invalidMessageFactory = (
    { amount, distribution, matrixAmount, numRange, inputTextLength, videoTestText, fiveSecTestText }: SurveyAddonAlert,
    question: QuestionContext,
): string => {
    const {
        answeredCount,
        answersActors,
        groups,
        invalid,
        params: { distributeScore, distributeSurplus, maxLength: max, minLength: min, dropdownType },
        type,
    } = question;
    const withMax = typeof max === 'number';
    const withMin = typeof min === 'number';

    switch (type) {
        case QuestionType.SELECT_FEW_IMAGE: {
            if ((withMax && answeredCount > max) || (withMin && answeredCount < min)) {
                return replaceMinMax(amount, min, max);
            }

            return null;
        }
        case QuestionType.DISTRIBUTE_SCALE: {
            const pointsSum = answersActors.reduce((acc, item) => {
                const { response } = item.getSnapshot().context;
                const { value } = response.getSnapshot().context;
                return acc + (value as number);
            }, 0);
            const points = parseInt(`${distributeScore}`);

            if (pointsSum !== points && invalid && distributeSurplus) {
                return replacePoints(distribution, points);
            }
            return null;
        }
        case QuestionType.MATRIX_SINGLE_ANSWER: {
            const answersNumber = groups.reduce(
                (acc, group) =>
                    acc +
                    Object.keys(group.responses).reduce(
                        (acc, key) => acc + (group.responses.get(Number(key)).value ? 1 : 0),
                        0,
                    ),
                0,
            );
            if ((withMin && answersNumber < min) || (withMax && answersNumber > max)) {
                return replaceMinMax(matrixAmount, min, max);
            }
            return null;
        }
        case QuestionType.TEST_FEW_OF_LIST:
        case QuestionType.FEW_OF_LIST:
        case QuestionType.DROPDOWN_LIST: {
            if (type === QuestionType.DROPDOWN_LIST && dropdownType !== 'multi') {
                return null;
            }

            const answeredCount = answersActors.reduce((acc, aActor) => {
                const { response } = aActor.getSnapshot().context;
                const { value } = response.getSnapshot().context;

                if (value) {
                    acc += 1;
                }

                return acc;
            }, 0);

            if ((withMin && answeredCount < min) || (withMax && answeredCount > max)) {
                return replaceMinMax(amount, min, max);
            }

            return null;
        }
        case QuestionType.MATRIX_FEW_ANSWERS: {
            if (
                (withMin && answersActors.every((a) => a.getSnapshot().context.answeredCount >= min)) ||
                (withMax && answersActors.every((a) => a.getSnapshot().context.answeredCount <= max))
            ) {
                return replaceMinMax(matrixAmount, min, max);
            }

            return null;
        }
        case QuestionType.NUMBER: {
            const api = getApiClass(QuestionType.NUMBER);

            if (!api.validate(question) && (withMin || withMax)) {
                return replaceMinMax(numRange, min, max);
            }

            return null;
        }
        case QuestionType.SHORT_TEXT:
        case QuestionType.LONG_TEXT: {
            if (
                (withMin && answersActors.every((a) => a.getSnapshot().context.value?.length >= min)) ||
                (withMax && answersActors.every((a) => a.getSnapshot().context.value?.length <= max))
            ) {
                return replaceMinMax(inputTextLength, min, max);
            }

            return null;
        }
        case QuestionType.VIDEO_TEST: {
            return videoTestText;
        }
        case QuestionType.FIVE_SEC_TEST: {
            return fiveSecTestText;
        }
        case QuestionType.FIGMA_TEST: {
            return 'Пожалуйста, завершите тест прототипа';
        }
        default:
            return null;
    }
};

export const styleToQuestionPosition = {
    right: css.end,
    left: css.start,
    center: css.center,
};

export interface SurveyQuestion {
    question: QuestionContext;
    actor: QuestionActorRef;
    readOnly?: boolean;
    questionBlockPosition?: AlignSetting;
    twisterReadOnly?: boolean;
}

export const substrateStyles = { backgroundColor: '#fff', borderRadius: '12px', padding: '14px' };
